import { api } from './api';

export interface ShippingAddress {
  readonly name: string;
  readonly line1: string;
  readonly line2: string | null;
  readonly city: string;
  readonly state: string;
  readonly postalCode: string;
  readonly country: string;
  readonly phone: string | null;
}

export interface BillingAddress {
  readonly name: string;
  readonly line1: string;
  readonly line2: string | null;
  readonly city: string;
  readonly state: string;
  readonly postalCode: string;
  readonly country: string;
}

export interface OrderPricing {
  readonly subtotal: number;
  readonly setupFee: number;
  readonly shippingCharge: number;
  readonly taxCharged: number;
  readonly discountAmount: number;
  readonly couponCode: string | null;
  readonly totalCharged: number;
  readonly currency: string;
  readonly stripeFee: number | null;
  readonly netPayout: number | null;
  readonly materialCostEstimate: number | null;
  readonly shippingLabelCost: number | null;
  readonly packagingCost: number | null;
  readonly totalInternalCost: number | null;
  readonly estimatedProfit: number | null;
  readonly actualProfit: number | null;
  readonly refundAmount: number | null;
}

export interface PrintJobDetails {
  readonly productName: string | null;
  readonly fileKey: string | null;
  readonly originalFileName: string | null;
  readonly materialId: string | null;
  readonly color: string | null;
  readonly nozzleSize: number | null;
  readonly layerHeight: number | null;
  readonly infillPercent: number | null;
  readonly wallCount: number | null;
  readonly supportsRequired: boolean | null;
  readonly brimRaft: 'brim' | 'raft' | 'none' | null;
  readonly estimatedFilamentGrams: number | null;
  readonly estimatedPrintTimeMinutes: number | null;
  readonly actualFilamentGrams: number | null;
  readonly actualPrintTimeMinutes: number | null;
  readonly dimensions: {
    readonly width: number;
    readonly height: number;
    readonly depth: number;
  } | null;
  readonly weightGrams: number | null;
  readonly processedFileKey: string | null;
}

export interface ProductionRecord {
  readonly slicerProfile: string | null;
  readonly printerProfile: string | null;
  readonly bambuPreset: string | null;
  readonly bedType: string | null;
  readonly materialBrand: string | null;
  readonly materialColorCode: string | null;
  readonly dryingRequired: boolean | null;
  readonly printStartTime: string | null;
  readonly printEndTime: string | null;
  readonly printSuccess: boolean | null;
  readonly failureCategory: string | null;
  readonly failureNotes: string | null;
  readonly reprintCount: number;
  readonly qcPass: boolean | null;
  readonly qcNotes: string | null;
  readonly printPhotos: readonly string[];
}

export interface ShippingDetails {
  readonly packagingStatus: 'pending' | 'packed' | 'labeled' | null;
  readonly carrier: string | null;
  readonly shippingService: string | null;
  readonly trackingNumber: string | null;
  readonly shippingLabelId: string | null;
  readonly dateShipped: string | null;
  readonly deliveryStatus: string | null;
  readonly deliveredDate: string | null;
}

export interface CommunicationLog {
  readonly confirmationEmailSent: boolean;
  readonly quoteResponseSent: boolean;
  readonly shipmentEmailSent: boolean;
  readonly lastCustomerContactDate: string | null;
  readonly lastAdminResponseDate: string | null;
  readonly supportNotes: string | null;
}

export interface LegalFlags {
  readonly termsAcceptedAt: string | null;
  readonly fileOwnershipConfirmed: boolean;
  readonly printabilityDisclaimerAccepted: boolean;
  readonly refundPolicyAccepted: boolean;
  readonly restrictedContentFlag: boolean;
  readonly copyrightConcernFlag: boolean;
  readonly safetyConcernFlag: boolean;
}

export interface OrderEvent {
  readonly status: string;
  readonly timestamp: string;
  readonly actor: string;
  readonly note: string | null;
}

export interface Order {
  // Identity
  readonly orderId: string;
  readonly userId: string;
  readonly type: 'quote' | 'product';
  readonly quoteId: string | null;
  readonly productId: string | null;
  readonly stripeCustomerId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly paymentConfirmedAt: string | null;

  // Customer
  readonly customerName: string | null;
  readonly customerEmail: string | null;
  readonly shippingAddress: ShippingAddress | null;
  readonly billingAddress: BillingAddress | null;
  readonly customerNotes: string | null;

  // Product / print job
  readonly materialId: string | null;
  readonly color: string | null;
  readonly quantity: number;
  readonly printJob: PrintJobDetails | null;

  // Pricing
  readonly totalPrice: number;
  readonly pricing: OrderPricing | null;

  // Workflow
  readonly status: string;
  readonly events: readonly OrderEvent[];
  readonly priority: 'normal' | 'rush' | 'low';
  readonly queuePosition: number | null;
  readonly dueBy: string | null;
  readonly estimatedCompletionDate: string | null;
  readonly holdReason: string | null;
  readonly batchId: string | null;
  readonly reprintRequired: boolean;
  readonly failureReason: string | null;
  readonly cancellationReason: string | null;
  readonly adminNotes: string | null;

  // Shipping
  readonly shipping: ShippingDetails | null;
  readonly trackingNumber: string | null;
  readonly trackingCarrier: string | null;

  // Production
  readonly production: ProductionRecord | null;

  // Communication
  readonly communication: CommunicationLog | null;

  // Legal
  readonly legal: LegalFlags | null;
}

export interface OrderListResponse {
  readonly data: readonly Order[];
  readonly pagination: { readonly lastKey: string | null };
}

export const ordersApi = {
  get: async (id: string): Promise<Order> => {
    const result = await api.get<Order>(`/orders/${id}`);
    if (!result.data) {
      throw new Error('Order not found');
    }
    return result.data;
  },

  list: async (params?: { lastKey?: string; limit?: number }): Promise<OrderListResponse> => {
    const query = new URLSearchParams();
    if (params?.lastKey) query.set('lastKey', params.lastKey);
    if (params?.limit) query.set('limit', String(params.limit));
    const qs = query.toString();
    const result = await api.get<readonly Order[]>(`/orders${qs ? `?${qs}` : ''}`);
    return {
      data: result.data ?? [],
      pagination: result.pagination ?? { lastKey: null },
    };
  },
};
